<script setup lang="ts">
defineOptions({
  name: 'GameLeaderboard'
});
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAuthStore } from '@/stores/auth'; // Highlight own name

interface LeaderboardEntry {
  user_id: string;
  score: number;
  rank: number;
  username?: string; // Optional if not sent in some cases, but plan says it is
}

interface Props {
  leaderboard: LeaderboardEntry[];
}

defineProps<Props>();
const { t } = useI18n();
const authStore = useAuthStore();
const myUserId = computed(() => authStore.user?.id ? String(authStore.user.id) : null);
</script>

<template>
  <div class="leaderboard-overlay">
    <div class="leaderboard-container">
      <h3>{{ t('leaderboard.intermediate_rank') }}</h3>
      <table class="leaderboard-table">
        <thead>
          <tr>
            <th class="rank-col">{{ t('leaderboard.rank') }}</th>
            <th class="name-col">{{ t('leaderboard.player') }}</th>
            <th class="score-col">{{ t('leaderboard.score') }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="entry in leaderboard" :key="entry.user_id" :class="{ 'my-row': entry.user_id === myUserId }">
            <td class="rank-col">
              <span v-if="entry.rank === 1" class="medal gold">🥇</span>
              <span v-else-if="entry.rank === 2" class="medal silver">🥈</span>
              <span v-else-if="entry.rank === 3" class="medal bronze">🥉</span>
              <span v-else>{{ entry.rank }}</span>
            </td>
            <td class="name-col">
              <!-- ToDo: Add avatar if available -->
              {{ entry.username || `Player ${entry.user_id}` }}
              <span v-if="entry.user_id === myUserId" class="you-badge">{{ t('leaderboard.you') }}</span>
            </td>
            <td class="score-col">{{ entry.score }}</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<style scoped>
.leaderboard-overlay {
  /* Overlay over the game area */
  position: fixed;
  /* or absolute relative to game container if we want */
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.7);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
  animation: fadeIn 0.3s ease-out;
}

.leaderboard-container {
  background-color: white;
  padding: 2rem;
  border-radius: 12px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  text-align: center;
}

h3 {
  margin-top: 0;
  color: #333;
  margin-bottom: 1.5rem;
  font-size: 1.5rem;
}

.leaderboard-table {
  width: 100%;
  border-collapse: collapse;
}

th {
  background-color: #f8f9fa;
  color: #666;
  font-weight: 600;
  padding: 0.75rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
}

td {
  padding: 0.75rem;
  border-bottom: 1px solid #dee2e6;
  text-align: left;
}

.rank-col {
  width: 60px;
  text-align: center;
  font-weight: bold;
}

.score-col {
  width: 80px;
  text-align: right;
  font-weight: bold;
}

.my-row {
  background-color: #e3f2fd;
  /* Light blue highlight */
  font-weight: bold;
}

.you-badge {
  font-size: 0.8rem;
  color: #666;
  margin-left: 5px;
}

.medal {
  font-size: 1.2rem;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}
</style>
